import { useMemo, useRef } from "react";
import moment, { Moment } from "moment";
import { useIntl } from "react-intl";
import { MutationFunctionOptions, OperationVariables } from "@apollo/client";
import type { FetchResult } from "@apollo/client/link/core";
import { useAppConfig } from "@ct-react/core";
import { bookingTranslations } from "../i18n/sharable-defs";
import { getRecaptchaToken } from "../code-splitting/others/recaptcha";

export const useSeason = () => {

  const today = useRef(moment());
  const summerLimit = useRef([
    moment(`${today.current.format("YYYY")}-04-01`),
    moment(`${today.current.format("YYYY")}-09-30`),
  ]);

  const isSummer = today.current.isBetween(summerLimit.current[0], summerLimit.current[1], "day", "[]");
  return isSummer ? "summer" : "winter";

}

export const usePickerLabelize = () => {

  const intl = useIntl();

  return useMemo(() => ({
    month: (m: Moment) => intl.formatDate(m.toDate(), { month: "long", year: "numeric" }),
    dayOfWeek: (d: Moment) => intl.formatDate(d.toDate(), { weekday: "short" }),
    onlineRestricted: intl.formatMessage(bookingTranslations.onDemandOnly)
  }), [ intl ]);

}

export const useApolloMore = () => {

  const { options: { recaptchaSiteKey } } = useAppConfig();

  const withRecaptcha = <TData = any, TVariables extends OperationVariables = OperationVariables>(
    mutation: (options?: MutationFunctionOptions<TData, TVariables>) => Promise<FetchResult<TData>>,
    action?: string) => {
    return async (options?: MutationFunctionOptions<TData, TVariables>): Promise<FetchResult<TData>> => {
      if (!recaptchaSiteKey)
        return mutation(options);

      const token = await getRecaptchaToken(recaptchaSiteKey, !!action ? { action } : undefined);
      return mutation({
        ...options,
        context: {
          ...options?.context,
          headers: { ...options?.context?.headers, "GQL-Consumer-Recaptcha-Token": token }
        }
      });
    }
  }

  return { mutation: { withRecaptcha } };

}
